import { z } from 'zod';
import { AppVersion } from './changelog.js';
import { Cents, IsoDateTime, Uuid } from './common.js';
import { TaskNotifyChannel } from './task-notify.js';

/**
 * Notifications (CDC §0, §3.2 `notifications` / `push_subscriptions`, §3.7) : elles INFORMENT,
 * elles ne déclenchent jamais d'action. SSE pour l'appli ouverte, Web Push pour l'iPhone.
 */

/** Sortes de notifications. `info` (tests, messages système) n'a pas d'interrupteur : elle part toujours. */
export const NotificationKind = z.enum([
  'quote_signed',
  'client_reply',
  'overdue',
  'team_message',
  'info',
  // Lot T : cartes ajoutées ou fermées par l'action de quelqu'un d'autre (ou par une synchro).
  'new_task',
  'task_closed',
  // Lot BB (contrat) : réponse prête pour une conversation de support, fournisseur important (lots BD et BG).
  'support',
  // Lot BF : rappels du coach de journée (créneau libre, bilan du soir).
  'coach',
]);
export type NotificationKind = z.infer<typeof NotificationKind>;

/** Interrupteurs Web Push par sorte (Réglages > « Sur iPhone » de la maquette). */
export const PushPreferences = z.object({
  /** Devis signé (avec le son de fête). */
  quoteSigned: z.boolean().default(true),
  /** Réponse d'un client. */
  clientReply: z.boolean().default(true),
  /** Action en retard qui m'est attribuée. */
  overdue: z.boolean().default(true),
  /** Message de l'équipe. */
  teamMessage: z.boolean().default(true),
  /** Lot T : nouvelle tâche pour moi, suite à l'action de quelqu'un d'autre ou à une synchro. */
  newTask: z.boolean().default(true),
  /** Lot T : une de mes tâches fermée par l'action de quelqu'un d'autre (dans l'appli seulement par défaut). */
  taskClosed: z.boolean().default(false),
  /** Lot BB : support (réponse prête pour une conversation qui m'est attribuée, alerte d'un fournisseur). */
  support: z.boolean().default(true),
  /** Lot BF : rappels du coach de journée (créneau libre, bilan du soir). */
  coach: z.boolean().default(true),
});
export type PushPreferences = z.infer<typeof PushPreferences>;

/** Lot T : horizon de « À faire » en mois (0 = tout afficher). */
export const TodoHorizonMonths = z.int().min(0).max(24);
export const DEFAULT_TODO_HORIZON_MONTHS = 3;
/** Choix proposés (Réglages et « À faire ») ; 0 = jamais masquer. */
export const TODO_HORIZON_CHOICES: readonly number[] = [1, 2, 3, 6, 12, 0];

/**
 * Lot BP : jours où le membre veut être notifié des tâches de la journée, numérotés comme `isoWeekday`
 * (1 = lundi, 7 = dimanche). Tout décocher est permis.
 */
export const NotifyDays = z
  .array(z.int().min(1).max(7))
  .max(7)
  .refine((days) => new Set(days).size === days.length, { message: 'Un jour apparaît deux fois.' });
export type NotifyDays = z.infer<typeof NotifyDays>;
export const DEFAULT_NOTIFY_DAYS: readonly number[] = [1, 2, 3, 4, 5];
/** Cases du réglage, dans l'ordre de la semaine. */
export const NOTIFY_DAY_CHOICES: readonly { day: number; short: string; label: string }[] = [
  { day: 1, short: 'L', label: 'lundi' },
  { day: 2, short: 'M', label: 'mardi' },
  { day: 3, short: 'M', label: 'mercredi' },
  { day: 4, short: 'J', label: 'jeudi' },
  { day: 5, short: 'V', label: 'vendredi' },
  { day: 6, short: 'S', label: 'samedi' },
  { day: 7, short: 'D', label: 'dimanche' },
];

/** Lot BR : cartes du Suivi. */
export const ProjectBoardView = z.enum(['simple', 'detail']);
export type ProjectBoardView = z.infer<typeof ProjectBoardView>;

/**
 * Préférences d'un membre (`users.preferences`, renvoyées par `GET /api/me`). Ajout au contrat
 * approuvé par le PM (lot R). Toute clé absente prend sa valeur par défaut : `{}` est valide.
 */
export const Preferences = z.object({
  /** Fanfare de la fête de signature dans l'appli. */
  sound: z.boolean().default(true),
  push: PushPreferences.prefault({}),
  /** Récap du matin à 7 h 30 (« Tes actions du jour », lot E2) : notification dans l'appli et Web Push. */
  morningRecap: z.boolean().default(true),
  /**
   * Lot AJ : le même récap par mail (à sa propre adresse, depuis la boîte partagée contact@), avec le détail des
   * actions. Exception validée par Maxime le 14/10/2026 à la règle « pas d'e-mail automatique » : mail interne.
   */
  morningRecapEmail: z.boolean().default(true),
  /**
   * Lot AN : la fiche R1 par mail (à sa propre adresse, depuis la boîte partagée), pour les R1 où le membre est
   * présent. Exception validée par Maxime le 15/10/2026 à la règle « pas d'e-mail automatique » : mail interne.
   */
  r1BriefEmail: z.boolean().default(true),
  /**
   * Lot T : dans « À faire », les tâches prévues dans plus de N mois sont repliées sous « Plus tard » (une
   * ligne « Afficher »). null ou 0 : tout afficher. Propre à chaque membre.
   */
  todoHorizonMonths: TodoHorizonMonths.nullable().default(DEFAULT_TODO_HORIZON_MONTHS),
  /**
   * Lot AZ « Le prévenir » : dernier canal choisi pour prévenir quelqu'un d'une tâche (Google Chat par défaut),
   * retenu à chaque envoi et proposé la fois suivante.
   */
  taskNotifyChannel: TaskNotifyChannel.default('chat'),
  /**
   * Lot BA : dernière version du Cockpit dont la personne a vu les nouveautés (« J'ai vu » du popup « Quoi de
   * neuf »). Gardée sur le serveur : fermé sur l'ordinateur, le popup ne revient pas sur le téléphone. null : tout
   * premier passage (le popup ne montre alors que la dernière version).
   */
  lastSeenVersion: AppVersion.nullable().default(null),
  /**
   * Lot BP : jours des notifications sur les tâches de la journée, tous canaux (récap du matin et son mail, rappels
   * du coach). Lundi à vendredi par défaut. Ne concerne pas les événements (nouvelle tâche, devis signé, réponse d'un
   * client, support, fiches R1). Un jour non retenu : aucun envoi, sans rattrapage le lendemain.
   */
  notifyDays: NotifyDays.default([...DEFAULT_NOTIFY_DAYS]),
  /** Lot BR : cartes du Suivi de la page Projets, vue simple (par défaut) ou détaillée. Propre à chaque membre. */
  projectBoardView: ProjectBoardView.default('simple'),
});
export type Preferences = z.infer<typeof Preferences>;

/** Valeurs par défaut (toutes les notifications, avec le son). */
export const DEFAULT_PREFERENCES: Preferences = Preferences.parse({});

/** `PATCH /api/me/preferences` : modification partielle, fusionnée avec l'existant. */
export const PreferencesPatch = z
  .object({
    sound: z.boolean().optional(),
    morningRecap: z.boolean().optional(),
    morningRecapEmail: z.boolean().optional(),
    r1BriefEmail: z.boolean().optional(),
    todoHorizonMonths: TodoHorizonMonths.nullable().optional(),
    taskNotifyChannel: TaskNotifyChannel.optional(),
    lastSeenVersion: AppVersion.nullable().optional(),
    notifyDays: NotifyDays.optional(),
    projectBoardView: ProjectBoardView.optional(),
    push: z
      .object({
        quoteSigned: z.boolean().optional(),
        clientReply: z.boolean().optional(),
        overdue: z.boolean().optional(),
        teamMessage: z.boolean().optional(),
        newTask: z.boolean().optional(),
        taskClosed: z.boolean().optional(),
        support: z.boolean().optional(),
        coach: z.boolean().optional(),
      })
      .strict()
      .optional(),
  })
  .strict();
export type PreferencesPatch = z.infer<typeof PreferencesPatch>;

/** Interrupteur de préférences qui gouverne chaque sorte (null : toujours envoyée). */
export const PUSH_PREFERENCE_OF_KIND: Record<NotificationKind, keyof PushPreferences | null> = {
  quote_signed: 'quoteSigned',
  client_reply: 'clientReply',
  overdue: 'overdue',
  team_message: 'teamMessage',
  info: null,
  new_task: 'newTask',
  task_closed: 'taskClosed',
  support: 'support',
  coach: 'coach',
};

/** Libellés des interrupteurs (maquette, carte « Sur iPhone »). */
export const PUSH_PREFERENCE_LABELS: Record<keyof PushPreferences, string> = {
  quoteSigned: 'Devis signé (avec le son de fête)',
  clientReply: "Réponse d'un client",
  overdue: "Action en retard qui m'est attribuée",
  teamMessage: "Message de l'équipe",
  newTask: 'Nouvelle tâche pour moi',
  taskClosed: "Une de mes tâches fermée par quelqu'un d'autre",
  support: "Support : réponse prête pour un client, alerte d'un fournisseur",
  coach: 'Rappels du coach (créneau libre, bilan du soir)',
};

/** Une notification du journal (`notifications`). */
export const Notification = z.object({
  id: Uuid,
  kind: NotificationKind,
  title: z.string(),
  body: z.string(),
  /** Chemin interne ouvert au clic (ex. `/projets?prepare=…`). */
  url: z.string().nullable(),
  readAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
});
export type Notification = z.infer<typeof Notification>;

/** `GET /api/notifications` : les 50 dernières et le nombre de non lues. */
export const NotificationList = z.object({
  items: z.array(Notification),
  unreadCount: z.int().min(0),
});
export type NotificationList = z.infer<typeof NotificationList>;

const MarkNotificationsReadFields = z.object({ ids: z.array(Uuid).max(200).optional() });
/** `POST /api/notifications/read` : sans `ids` (ou sans corps), tout est marqué lu. */
export const MarkNotificationsReadBody = MarkNotificationsReadFields.nullish().transform(
  (body) => body ?? {},
);
export type MarkNotificationsReadBody = z.input<typeof MarkNotificationsReadFields>;

export const MarkNotificationsReadResult = z.object({ unreadCount: z.int().min(0) });
export type MarkNotificationsReadResult = z.infer<typeof MarkNotificationsReadResult>;

/**
 * Services de push acceptés (Apple, Google, Mozilla, Microsoft) : le serveur n'envoie jamais de
 * requête vers une adresse arbitraire fournie par un navigateur.
 */
export const PUSH_SERVICE_HOSTS = [
  'push.apple.com',
  'fcm.googleapis.com',
  'android.googleapis.com',
  'push.services.mozilla.com',
  'notify.windows.com',
] as const;

export function isAllowedPushEndpoint(endpoint: string): boolean {
  let url: URL;
  try {
    url = new URL(endpoint);
  } catch {
    return false;
  }
  if (url.protocol !== 'https:') return false;
  const host = url.hostname.toLowerCase();
  return PUSH_SERVICE_HOSTS.some((allowed) => host === allowed || host.endsWith(`.${allowed}`));
}

/** `POST /api/push/subscribe` : le `PushSubscription.toJSON()` du navigateur. */
export const PushSubscriptionBody = z.object({
  endpoint: z
    .string()
    .max(2048)
    .refine(isAllowedPushEndpoint, { message: 'Adresse de service de push non reconnue' }),
  expirationTime: z.number().nullable().optional(),
  keys: z.object({
    p256dh: z.string().min(1).max(512),
    auth: z.string().min(1).max(512),
  }),
});
export type PushSubscriptionBody = z.input<typeof PushSubscriptionBody>;

/** `DELETE /api/push/subscribe`. */
export const PushUnsubscribeBody = z.object({ endpoint: z.string().min(1).max(2048) });
export type PushUnsubscribeBody = z.infer<typeof PushUnsubscribeBody>;

/**
 * `GET /api/push/config` : clé publique VAPID (null si le push n'est pas configuré) et
 * disponibilité de la signature simulée (développement et démo seulement).
 */
export const PushConfig = z.object({
  enabled: z.boolean(),
  publicKey: z.string().nullable(),
  simulateSignature: z.boolean(),
});
export type PushConfig = z.infer<typeof PushConfig>;

/** `POST /api/push/test` : nombre d'appareils joints. */
export const PushTestResult = z.object({
  enabled: z.boolean(),
  subscriptions: z.int().min(0),
  delivered: z.int().min(0),
});
export type PushTestResult = z.infer<typeof PushTestResult>;

/** Événement SSE `quote.signed` (`GET /api/stream`) : déclenche la fête de signature. */
export const QuoteSignedEvent = z.object({
  quoteId: Uuid,
  clientName: z.string().min(1),
  amountCents: Cents,
  description: z.string(),
});
export type QuoteSignedEvent = z.infer<typeof QuoteSignedEvent>;

const SimulateSignatureFields = z.object({
  clientName: z.string().trim().min(1).max(120).optional(),
  amountCents: Cents.min(0).optional(),
  description: z.string().max(200).optional(),
});
/** `POST /api/dev/simulate-signature` (développement et démo seulement) : valeurs facultatives, corps facultatif. */
export const SimulateSignatureBody = SimulateSignatureFields.nullish().transform((body) => body ?? {});
export type SimulateSignatureBody = z.input<typeof SimulateSignatureFields>;
